/**
 * §16 — Content tables. Loaded once at boot, deep-frozen, hashed into `content_hash` (A.6).
 * Cross-references are by ID only, never by display name (spine §16).
 */

import { fnv1a32 } from './rng';

import weaponsRaw from '../content/weapons.json';
import enemiesRaw from '../content/enemies.json';
import bossesRaw from '../content/bosses.json';
import passivesRaw from '../content/passives.json';
import evolutionsRaw from '../content/evolutions.json';
import synergiesRaw from '../content/synergies.json';
import pickupsRaw from '../content/pickups.json';
import statusRaw from '../content/status-effects.json';
import vfxRaw from '../content/vfx.json';
import phasesRaw from '../content/wave-phases.json';
import audioRaw from '../content/audio-cues.json';
import difficultyRaw from '../content/difficulty-modifiers.json';
import gachaRaw from '../content/gacha.json';
import shipFormRaw from '../content/ship-form.json';
import captainTraitsRaw from '../content/captain-traits.json';

export interface WeaponStats {
  damage?: number;
  rate_per_s?: number;
  speed_u_per_s?: number;
  count?: number;
  area?: { impact_radius?: number; radius?: number; width?: number; length?: number; arc_deg?: number };
  duration_s?: number;
  penetration?: number;
  ricochet?: number;
  range_u?: number;
  knockback?: number;
  crit_chance?: number;
  crit_multiplier?: number;
  tick_rate_hz?: number;
  [k: string]: unknown;
}

export interface WeaponDef {
  id: string;
  version: string;
  class: string;
  display_name: string;
  combat_role: string;
  slot: string;
  base_stats: WeaponStats;
  levels: { level: number; set: WeaponStats }[];
  evolution_id?: string;
  linked_passive_id?: string;
  targeting_policy: string;
  perf_cost?: { projectiles_per_s?: number; particles_per_s?: number };
  acceptance_criteria?: string | string[];
  [k: string]: unknown;
}

export interface EnemyDef {
  id: string;
  version: string;
  role: string;
  display_name: string;
  silhouette: { shape: string; hue: string; hue_name: string };
  spawn_window_min: [number, number];
  stats: {
    hp_base: number;
    hp_at_intro?: number;
    contact_damage_base: number;
    move_speed: number;
    [k: string]: unknown;
  };
  steering: {
    max_force: number;
    turn_rate_deg_s: number;
    weights: {
      separation: number;
      alignment: number;
      cohesion: number;
      pursuit: number;
      avoidance: number;
      formation: number;
    };
    scripted: boolean;
    [k: string]: unknown;
  };
  attack: { type: string; damage?: number; [k: string]: unknown };
  telegraph: { form: string; duration_ms: number };
  formation: string[];
  counterplay: string;
  drops: { xp_motes: number; health_chance: number; powerup_chance: number };
  threat_score: number;
  pop_budget: number;
  [k: string]: unknown;
}

export interface EliteModifier {
  version: string;
  window_opens_min: number;
  multipliers: { hp: number; contact_damage: number; move_speed: number; scale: number; xp: number; threat_cost: number };
  affix_count: { default: number; descent_2_plus: number };
  [k: string]: unknown;
}

export interface AffixDef {
  id: string;
  name: string;
  effect: Record<string, number>;
  tell: string;
  threat_cost: number;
  [k: string]: unknown;
}

export interface BossDef {
  id: string;
  display_name: string;
  [k: string]: unknown;
}

export interface PassiveDef {
  id: string;
  display_name: string;
  [k: string]: unknown;
}

export interface EvolutionDef {
  id: string;
  display_name: string;
  [k: string]: unknown;
}

export interface StatusDef {
  id: string;
  [k: string]: unknown;
}

export interface PhaseDef {
  id: string;
  [k: string]: unknown;
}

export interface VfxDef {
  id: string;
  [k: string]: unknown;
}

export interface AudioCueDef {
  id: string;
  [k: string]: unknown;
}

export interface PickupDef {
  id: string;
  [k: string]: unknown;
}

export interface SynergyDef {
  id: string;
  [k: string]: unknown;
}

export interface DifficultyTier {
  id: string;
  [k: string]: unknown;
}

function index<T extends { id: string }>(list: readonly T[]): ReadonlyMap<string, T> {
  const m = new Map<string, T>();
  for (const item of list) m.set(item.id, item);
  return m;
}

export const WEAPONS = (weaponsRaw as unknown as { weapons: WeaponDef[] }).weapons;
export const ENEMIES = (enemiesRaw as unknown as { enemies: EnemyDef[] }).enemies;
export const ELITE_MODIFIER = (enemiesRaw as unknown as { elite_modifier: EliteModifier }).elite_modifier;
export const AFFIXES = (enemiesRaw as unknown as { affixes: AffixDef[] }).affixes;
export const BOSSES = (bossesRaw as unknown as { bosses: BossDef[] }).bosses;
export const PASSIVES = (passivesRaw as unknown as { passives: PassiveDef[] }).passives;
export const EVOLUTIONS = (evolutionsRaw as unknown as { evolutions: EvolutionDef[] }).evolutions;
export const SYNERGIES = (synergiesRaw as unknown as { synergies: SynergyDef[] }).synergies;
export const PICKUPS = (pickupsRaw as unknown as { pickups: PickupDef[] }).pickups;
export const POWERUP_POOL = (pickupsRaw as unknown as { powerup_pool: Record<string, unknown> }).powerup_pool;
export const STATUS_EFFECTS = (statusRaw as unknown as { status_effects: StatusDef[] }).status_effects;
export const VFX = (vfxRaw as unknown as { vfx: VfxDef[] }).vfx;
export const PHASES = (phasesRaw as unknown as { phases: PhaseDef[] }).phases;
export const AUDIO_CUES = (audioRaw as unknown as { audio_cues: AudioCueDef[] }).audio_cues;
export const DIFFICULTY_TIERS = (difficultyRaw as unknown as { tiers: DifficultyTier[] }).tiers;

/** AMD-50 — the ship's trait point-buy and its mark stations. See `src/sim/ship-form`. */
export interface ShipFormTable {
  version: string;
  point_buy: {
    total_points: number;
    baseline_per_trait: number;
    base_pull: number;
    unit_pull_per_weapon_level: number;
    unit_pull_per_passive_rank: number;
    stat_pct_per_point: number;
  };
  mark_roles: { weapon_slots: string[]; passive_families: string[]; wear: string[] };
  weapons: { id: string; pull: number[]; glyph: string }[];
  passive_stats: { stat: string; pull: number[]; station: string }[];
  wear: {
    hp_per_bite: number;
    max_bites: number;
    bite_levels: number;
    wear_stations: number;
    kills_per_step: number;
    max_kill_steps: number;
    boss_kill_steps: number;
  };
}

export const SHIP_FORM = (shipFormRaw as unknown as { ship_form: ShipFormTable }).ship_form;

/**
 * The captain's boon / flaw pair and its flavour lines. See `src/captain/traits.ts`.
 *
 * Gameplay: both traits land in `recomputeStats`' ADD buckets, so this table is hashed with the
 * rest of §16 below — tuning a magnitude has to invalidate the replays whose meaning it changed.
 */
export interface CaptainTraitTable {
  version: string;
  pricing: {
    unit: string;
    note: string;
    weights: { id: string; label: string; scale: number; odds: number }[];
  };
  boons: {
    id: string;
    stat: string;
    label: string;
    unit: string;
    precision: number;
    magnitude: number;
    display_magnitude: number;
    note: string;
  }[];
  flaws: CaptainTraitTable['boons'];
  flavour: Record<string, string[]>;
}

export const CAPTAIN_TRAITS = (captainTraitsRaw as unknown as { captain_traits: CaptainTraitTable })
  .captain_traits;

export const weaponById = index(WEAPONS);
export const enemyById = index(ENEMIES);
export const bossById = index(BOSSES);
export const passiveById = index(PASSIVES);
export const evolutionById = index(EVOLUTIONS);
export const statusById = index(STATUS_EFFECTS);
export const phaseById = index(PHASES);
export const vfxById = index(VFX);
export const audioCueById = index(AUDIO_CUES);
export const affixById = index(AFFIXES);
export const pickupById = index(PICKUPS);
export const synergyById = index(SYNERGIES);
export const difficultyById = index(DIFFICULTY_TIERS);

/** Stable canonical JSON for hashing — keys sorted, no whitespace. */
function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonical((value as Record<string, unknown>)[k])).join(',') + '}';
}

/** A.6 / §15.2 — content_hash invalidates old replays and snapshots when tuning changes. */
export function computeContentHash(configSnapshot: unknown): string {
  const payload = canonical([
    weaponsRaw,
    enemiesRaw,
    bossesRaw,
    passivesRaw,
    evolutionsRaw,
    synergiesRaw,
    pickupsRaw,
    statusRaw,
    phasesRaw,
    difficultyRaw,
    gachaRaw,
    // AMD-50 — these three were imported and read at runtime but absent from the hash, so a
    // `vfx.json` or `audio-cues.json` edit could not invalidate a replay it had changed the
    // meaning of. `ship-form.json` is gameplay (it moves three stats), so it joins them here.
    vfxRaw,
    audioRaw,
    shipFormRaw,
    // The captain's boon / flaw pair moves the same ADD buckets a passive rank moves, so it is
    // gameplay by the same test `ship-form.json` passed, and it joins the hash for the same reason.
    captainTraitsRaw,
    configSnapshot,
  ]);
  return fnv1a32(payload).toString(16).padStart(8, '0');
}

/**
 * §6.1 — upgrade deltas are absolute set-values per level.
 * Resolves a weapon's stats at a given level by applying each level's `set` in order.
 */
export function resolveWeaponStats(def: WeaponDef, level: number): WeaponStats {
  const out: WeaponStats = { ...def.base_stats };
  if (def.base_stats.area) out.area = { ...def.base_stats.area };
  for (const lv of def.levels) {
    if (lv.level > level) break;
    for (const [k, v] of Object.entries(lv.set)) {
      if (k === 'area' && typeof v === 'object' && v !== null) {
        out.area = { ...out.area, ...(v as Record<string, number>) };
      } else {
        (out as Record<string, unknown>)[k] = v;
      }
    }
  }
  return out;
}

export const MAX_WEAPON_LEVEL = 5;
